import { Button } from '@heidi/design-system/button';
import { Skeleton } from '@heidi/design-system/skeleton';
import { StatusIndicator } from '@heidi/design-system/status-indicator';
import {
  TypographyCaption,
  TypographyCaptionBold,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronDown, ExternalLink, Loader2, Square } from 'lucide-react';
import type { ReactNode } from 'react';
import { type IntlShape, useIntl } from 'react-intl';

import { childRunInlinePanelMessages } from './child-run-inline-panel.messages';

export type ChildRunStatus = 'running' | 'done' | 'error' | 'cancelled';

export interface ChildRunInlinePanelProps {
  childAgentName?: string;
  status?: ChildRunStatus;
  isLoading?: boolean;
  unavailable?: boolean;
  awaitingChildRunId?: boolean;
  href?: string;
  expanded?: boolean;
  canExpand?: boolean;
  onToggle?: () => void;
  /**
   * Shown only while running with a landed child run id — an awaiting row
   * has nothing to cancel, and the CUA overlay stop button never opens for
   * a headless child.
   */
  onStop?: () => void;
  /** Disabled while the cancel is in flight. */
  stopping?: boolean;
  children?: ReactNode;
}

export const ChildRunInlinePanel = ({
  childAgentName,
  status = 'running',
  isLoading = false,
  unavailable = false,
  awaitingChildRunId = false,
  href,
  expanded = false,
  canExpand = false,
  onToggle,
  onStop,
  stopping = false,
  children,
}: ChildRunInlinePanelProps) => {
  const intl = useIntl();
  const working = awaitingChildRunId || status === 'running';
  const errored = !awaitingChildRunId && status === 'error';
  const cancelled = !awaitingChildRunId && status === 'cancelled';
  return (
    <div className="rounded-xl border border-border bg-surface-container p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {working ? (
            <Loader2 className="size-4 shrink-0 animate-spin text-text-tertiary" />
          ) : errored ? (
            <StatusIndicator status="alert" size="xs" />
          ) : cancelled ? (
            <StatusIndicator status="archive" size="xs" />
          ) : (
            <StatusIndicator status="done" size="xs" />
          )}
          <div className="min-w-0">
            <TypographyCaptionBold className="truncate text-text-primary">
              {childAgentName
                ? humanizeAgentName(childAgentName)
                : intl.formatMessage(childRunInlinePanelMessages.defaultAgentName)}
            </TypographyCaptionBold>
            <TypographyCaption
              className={cn(
                'block',
                errored ? 'text-validation-destructive' : 'text-text-tertiary'
              )}
            >
              {awaitingChildRunId
                ? intl.formatMessage(childRunInlinePanelMessages.starting)
                : status === 'running'
                  ? intl.formatMessage(childRunInlinePanelMessages.working)
                  : errored
                    ? intl.formatMessage(childRunInlinePanelMessages.failed)
                    : cancelled
                      ? intl.formatMessage(childRunInlinePanelMessages.cancelled)
                      : intl.formatMessage(childRunInlinePanelMessages.done)}
            </TypographyCaption>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onStop && status === 'running' && !awaitingChildRunId && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={onStop}
              disabled={stopping}
              className="shrink-0"
            >
              {/* DS Button (sm) already gaps children and sizes svgs —
                  restating them double-spaces and jumps 2px on icon swap. */}
              {stopping ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Square className="fill-current" />
              )}
              {intl.formatMessage(
                stopping
                  ? childRunInlinePanelMessages.stopping
                  : childRunInlinePanelMessages.stop
              )}
            </Button>
          )}
          {href && isSafeHref(href) && (
            <Button asChild size="sm" variant="secondary">
              <a href={href} target="_blank" rel="noreferrer">
                <ExternalLink className="me-1.5 size-3.5" />
                {intl.formatMessage(childRunInlinePanelMessages.openRun)}
              </a>
            </Button>
          )}
          {canExpand && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onToggle}
              aria-expanded={expanded}
            >
              <ChevronDown
                className={cn(
                  'me-1.5 size-3.5 transition-transform',
                  expanded && 'rotate-180'
                )}
              />
              {expanded
                ? intl.formatMessage(childRunInlinePanelMessages.hide)
                : intl.formatMessage(childRunInlinePanelMessages.show)}
            </Button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-3">
          {unavailable ? (
            <TypographyCaption className="block text-text-secondary">
              {intl.formatMessage(childRunInlinePanelMessages.runUnavailable)}
            </TypographyCaption>
          ) : isLoading ? (
            <ChildRunSkeleton intl={intl} />
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
};

// Capitalizes only the first letter of each word so acronyms (e.g. `GP`) survive.
const humanizeAgentName = (name: string): string =>
  name
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');

// Consumer-supplied href rendered into an anchor: reject anything that isn't
// http(s) or a relative path (e.g. a `javascript:` URL).
const isSafeHref = (href: string): boolean => {
  try {
    const url = new URL(href, 'https://harness.local');
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const ChildRunSkeleton = ({ intl }: { intl: IntlShape }) => (
  <div
    className="flex flex-col gap-2"
    aria-label={intl.formatMessage(childRunInlinePanelMessages.openingRunSkeleton)}
  >
    <Skeleton className="h-4 w-2/3" />
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-5/6" />
  </div>
);
